package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** Mentions expose display labels only; workspace email addresses are unnecessary for canvas feedback. */
@Serializable
data class CanvasMentionDto(val userId: String, val label: String)

/** Body and labels are untrusted plain text, never markup. */
@Serializable
data class CanvasCommentDto(
    val id: String,
    val authorId: String?,
    val authorLabel: String,
    val body: String,
    val mentions: List<CanvasMentionDto> = emptyList(),
    val createdAt: String,
)

/** An immutable version and anchor keep replies attached to what a reviewer actually saw. */
@Serializable
data class CanvasCommentThreadDto(
    val id: String,
    val designVersion: Int,
    val slotId: String,
    val slotKey: String,
    val locale: String,
    val layerId: String? = null,
    val resolved: Boolean,
    val revision: Int,
    val comments: List<CanvasCommentDto> = emptyList(),
    val createdAt: String,
    val updatedAt: String,
)

@Serializable
enum class CanvasSlotMarkState { APPROVED, CHANGES_REQUESTED }

/** An individual's mark binds image bytes; it does not create or authorize a set approval. */
@Serializable
data class CanvasSlotMarkDto(
    val id: String,
    val designVersion: Int,
    val slotId: String,
    val slotKey: String,
    val locale: String,
    val deviceClass: DeviceClass,
    val renderId: String,
    val sha256: String,
    val state: CanvasSlotMarkState,
    val reviewerId: String,
    val reviewerLabel: String,
    val revision: Int,
    val valid: Boolean,
    val createdAt: String,
    val updatedAt: String,
)

@Serializable
data class CanvasReviewSummaryDto(
    val openThreads: Int,
    val approvedMarks: Int,
    val changesRequestedMarks: Int,
    val staleMarks: Int,
)

@Serializable
data class CanvasReviewDto(
    val designVersion: Int,
    val threads: List<CanvasCommentThreadDto> = emptyList(),
    val marks: List<CanvasSlotMarkDto> = emptyList(),
    val summary: CanvasReviewSummaryDto,
)

/** [parentId] identifies the root thread; replies must repeat that thread's exact anchor. */
@Serializable
data class CreateCanvasCommentRequest(
    val slotId: String,
    val locale: String,
    val layerId: String? = null,
    val body: String,
    val mentions: List<String> = emptyList(),
    val parentId: String? = null,
)

@Serializable
data class SetCanvasThreadStateRequest(val baseRevision: Int, val resolved: Boolean)

/** A missing [baseRevision] creates a first mark; an existing mark always requires its current revision. */
@Serializable
data class PutCanvasSlotMarkRequest(
    val slotId: String,
    val locale: String,
    val deviceClass: DeviceClass,
    val renderId: String,
    val sha256: String,
    val state: CanvasSlotMarkState,
    val baseRevision: Int? = null,
)
